
// Define available locales
export const locales = ['en', 'es'] as const;
export type Locale = typeof locales[number];

// Get the base URL for the site
export const SITE_URL = (() => {
  // In browser environment
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  // In server environment (if ever used server-side)
  // Note: process.env is not available in browser, so we use a fallback
  return 'https://primehaus.py';
})();

/**
 * Generates hreflang alternate links for all supported languages
 * This tells Google about language variations of the page
 */
export function generateHreflangLinks(path: string = ''): { hrefLang: string; href: string }[] {
  const links: { hrefLang: string; href: string }[] = [];

  // Add links for each locale
  locales.forEach((locale) => {
    links.push({
      hrefLang: locale,
      href: `${SITE_URL}/${locale}${path}`,
    });
  });

  // Add x-default for international targeting
  links.push({
    hrefLang: 'x-default',
    href: `${SITE_URL}/es${path}`, // Spanish as default
  });

  return links;
}

/**
 * Generates alternate language links and canonical URL
 */
export function generateAlternates(path: string = '') {
  const languages: Record<string, string> = {};

  locales.forEach((locale) => {
    languages[locale] = `${SITE_URL}/${locale}${path}`;
  });

  return {
    canonical: `${SITE_URL}${path}`,
    languages,
  };
}

/**
 * Updates document head with SEO metadata
 */
interface SEOConfig {
  title: string;
  description: string;
  keywords?: string[];
  locale: Locale;
  path?: string;
  ogImage?: string;
  type?: 'website' | 'article';
}

export function updateSEOMetadata({
  title,
  description,
  keywords = [],
  locale,
  path = '',
  ogImage = '/og-image.jpg',
  type = 'website',
}: SEOConfig) {
  const url = `${SITE_URL}/${locale}${path}`;

  // Default keywords for real estate SEO - optimized for search intent
  const defaultKeywords = locale === 'es'
    ? [
        'inversión inmobiliaria Paraguay',
        'bienes raíces Paraguay',
        'propiedades comerciales Paraguay',
        'desarrolladora inmobiliaria Paraguay',
        'inversión extranjera Paraguay',
        'activos comerciales Paraguay',
        'uso mixto Paraguay',
        'régimen fiscal Paraguay',
        'rendimiento alquiler Paraguay',
        'Asunción bienes raíces',
        'Prime Haus Paraguay',
      ]
    : [
        'real estate investment Paraguay',
        'Paraguay real estate',
        'commercial properties Paraguay',
        'property developer Paraguay',
        'foreign investment Paraguay',
        'commercial real estate Paraguay',
        'mixed-use development Paraguay',
        'tax regime Paraguay',
        'rental yield Paraguay',
        'Asunción real estate',
        'Prime Haus Paraguay',
      ];

  const allKeywords = [...defaultKeywords, ...keywords].join(', ');

  // Update title
  document.title = title;

  // Update or create meta description
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute('content', description);

  // Update or create meta keywords
  let metaKeywords = document.querySelector('meta[name="keywords"]');
  if (!metaKeywords) {
    metaKeywords = document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    document.head.appendChild(metaKeywords);
  }
  metaKeywords.setAttribute('content', allKeywords);

  // Update or create author meta
  let metaAuthor = document.querySelector('meta[name="author"]');
  if (!metaAuthor) {
    metaAuthor = document.createElement('meta');
    metaAuthor.setAttribute('name', 'author');
    document.head.appendChild(metaAuthor);
  }
  metaAuthor.setAttribute('content', 'Prime Haus');

  // Update or create viewport meta
  let metaViewport = document.querySelector('meta[name="viewport"]');
  if (!metaViewport) {
    metaViewport = document.createElement('meta');
    metaViewport.setAttribute('name', 'viewport');
    metaViewport.setAttribute('content', 'width=device-width, initial-scale=1');
    document.head.appendChild(metaViewport);
  }

  // Update or create canonical link
  let canonicalLink = document.querySelector('link[rel="canonical"]');
  if (!canonicalLink) {
    canonicalLink = document.createElement('link');
    canonicalLink.setAttribute('rel', 'canonical');
    document.head.appendChild(canonicalLink);
  }
  canonicalLink.setAttribute('href', `${SITE_URL}${path}`);

  // Update or create Open Graph tags
  const ogTags = [
    { property: 'og:title', content: title },
    { property: 'og:description', content: description },
    { property: 'og:url', content: url },
    { property: 'og:site_name', content: 'Prime Haus' },
    { property: 'og:locale', content: locale === 'es' ? 'es_PY' : 'en_US' },
    { property: 'og:type', content: type },
    { property: 'og:image', content: `${SITE_URL}${ogImage}` },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { property: 'og:image:alt', content: title },
  ];

  ogTags.forEach(tag => {
    let ogTag = document.querySelector(`meta[property="${tag.property}"]`);
    if (!ogTag) {
      ogTag = document.createElement('meta');
      ogTag.setAttribute('property', tag.property);
      document.head.appendChild(ogTag);
    }
    ogTag.setAttribute('content', tag.content);
  });

  // Update or create Twitter Card tags
  const twitterTags = [
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:image', content: `${SITE_URL}${ogImage}` },
    { name: 'twitter:creator', content: '@primehaus' },
    { name: 'twitter:site', content: '@primehaus' },
  ];

  twitterTags.forEach(tag => {
    let twitterTag = document.querySelector(`meta[name="${tag.name}"]`);
    if (!twitterTag) {
      twitterTag = document.createElement('meta');
      twitterTag.setAttribute('name', tag.name);
      document.head.appendChild(twitterTag);
    }
    twitterTag.setAttribute('content', tag.content);
  });

  // Update or create robots meta
  let metaRobots = document.querySelector('meta[name="robots"]');
  if (!metaRobots) {
    metaRobots = document.createElement('meta');
    metaRobots.setAttribute('name', 'robots');
    document.head.appendChild(metaRobots);
  }
  metaRobots.setAttribute('content', 'index, follow');

  // Update or create hreflang links
  // Remove existing hreflang links first
  const existingHreflangs = document.querySelectorAll('link[hreflang]');
  existingHreflangs.forEach(link => link.remove());

  // Add new hreflang links
  const hreflangLinks = generateHreflangLinks(path);
  hreflangLinks.forEach(link => {
    const hreflangLink = document.createElement('link');
    hreflangLink.setAttribute('rel', 'alternate');
    hreflangLink.setAttribute('hreflang', link.hrefLang);
    hreflangLink.setAttribute('href', link.href);
    document.head.appendChild(hreflangLink);
  });

  // Add geographic meta tags
  const geoTags = [
    { name: 'geo.region', content: 'PY' },
    { name: 'geo.placename', content: 'Asunción' },
    { name: 'geo.position', content: '-25.2637;-57.5759' },
    { name: 'ICBM', content: '-25.2637, -57.5759' },
  ];

  geoTags.forEach(tag => {
    let geoTag = document.querySelector(`meta[name="${tag.name}"]`);
    if (!geoTag) {
      geoTag = document.createElement('meta');
      geoTag.setAttribute('name', tag.name);
      document.head.appendChild(geoTag);
    }
    geoTag.setAttribute('content', tag.content);
  });
}

/**
 * Generate structured data (JSON-LD) for real estate organization
 */
export function generateOrganizationSchema(locale: Locale) {
  return {
    '@context': 'https://schema.org',
    '@type': 'RealEstateAgent',
    '@id': `${SITE_URL}/#organization`,
    name: 'Prime Haus',
    alternateName: 'Prime Haus Paraguay',
    description: locale === 'es'
      ? 'Desarrolladora inmobiliaria premium de grado institucional en Paraguay especializada en activos comerciales y de uso mixto'
      : 'Premium institutional-grade real estate developer in Paraguay specializing in commercial and mixed-use assets',
    url: `${SITE_URL}/${locale}`,
    logo: {
      '@type': 'ImageObject',
      url: `${SITE_URL}/logo.png`,
      width: 250,
      height: 60,
    },
    image: `${SITE_URL}/og-image.jpg`,
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'PY',
      addressLocality: 'Asunción',
      addressRegion: 'Central',
      streetAddress: 'Distrito Financiero',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '-25.2637',
      longitude: '-57.5759',
    },
    contactPoint: [
      {
        '@type': 'ContactPoint',
        contactType: 'customer service',
        email: 'invest@primehaus.py',
        telephone: '+595-972-350400',
        availableLanguage: ['Spanish', 'English'],
        areaServed: ['PY', 'BR', 'AR', 'US', 'EU'],
      },
    ],
    sameAs: [
      // Add your social media profiles here when available
      // 'https://www.linkedin.com/company/primehaus',
      // 'https://www.instagram.com/primehaus',
      // 'https://www.facebook.com/primehaus',
    ],
    foundingDate: '2020',
    numberOfEmployees: {
      '@type': 'QuantitativeValue',
      value: 25,
    },
    slogan: locale === 'es'
      ? 'Disciplina europea, oportunidad latinoamericana'
      : 'European discipline, Latin American opportunity',
    priceRange: '$$$',
    areaServed: {
      '@type': 'Country',
      name: 'Paraguay',
    },
  };
}

/**
 * Generate LocalBusiness schema for better local SEO
 */
export function generateLocalBusinessSchema(locale: Locale) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: 'Prime Haus',
    image: `${SITE_URL}/og-image.jpg`,
    '@id': `${SITE_URL}/#localbusiness`,
    url: `${SITE_URL}/${locale}`,
    telephone: '+595-972-350400',
    priceRange: '$$$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Distrito Financiero',
      addressLocality: 'Asunción',
      postalCode: '1209',
      addressCountry: 'PY',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: -25.2637,
      longitude: -57.5759,
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
    sameAs: [
      // Add social media when available
    ],
  };
}

/**
 * Generate WebSite schema for sitelinks search box
 */
export function generateWebSiteSchema(locale: Locale) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}/#website`,
    url: `${SITE_URL}/${locale}`,
    name: 'Prime Haus',
    description: locale === 'es'
      ? 'Desarrolladora inmobiliaria de grado institucional en Paraguay'
      : 'Institutional-grade real estate developer in Paraguay',
    publisher: {
      '@id': `${SITE_URL}/#organization`,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/${locale}/propiedades?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
    inLanguage: locale === 'es' ? 'es-PY' : 'en-US',
  };
}

/**
 * Generate structured data for a property listing
 */
export function generatePropertySchema(property: any, locale: Locale) {
  const schema: any = {
    '@context': 'https://schema.org',
    '@type': property.propertyType?.includes('commercial') ? 'CommercialRealEstateListing' : 'ResidentialRealEstateListing',
    name: property.name || property.title,
    description: property.description,
    url: `${SITE_URL}/${locale}/propiedad/${property.slug || property.id}`,
    image: property.image || property.images?.[0],
    address: {
      '@type': 'PostalAddress',
      streetAddress: property.address,
      addressLocality: property.city || property.location,
      addressRegion: property.state,
      postalCode: property.zipCode,
      addressCountry: 'PY',
    },
    ...(property.price && {
      offers: {
        '@type': 'Offer',
        price: property.price.toString(),
        priceCurrency: property.currency || 'USD',
        priceSpecification: {
          '@type': 'PriceSpecification',
          price: property.price.toString(),
          priceCurrency: property.currency || 'USD',
          valueAddedTaxIncluded: 'false'
        }
      },
    }),
    ...(property.bedrooms && { numberOfBedrooms: property.bedrooms }),
    ...(property.bathrooms && { numberOfBathrooms: property.bathrooms }),
    ...(property.halfBaths && { numberOfPartialBathrooms: property.halfBaths }),
    ...(property.sqft && { floorSize: {
      '@type': 'QuantitativeValue',
      value: property.sqft,
      unitCode: 'FTK'
    }}),
    ...(property.lotSize && { hasMap: property.lotSize }),
    ...(property.yearBuilt && { yearBuilt: property.yearBuilt }),
    ...(property.propertyType && {
      category: property.propertyType
    }),
    ...(property.listingType && {
      category: `${property.listingType} property`
    }),
    ...(property.features && property.features.length > 0 && {
      amenityFeature: property.features
    }),
    ...(property.parking && {
      parking: `${property.parking} spaces`
    }),
    ...(property.hoaFees && {
      appliesToPaymentMethod: `HOA fees: $${property.hoaFees}/month`
    }),
    ...(property.floorNumber && {
      floorLevel: property.floorNumber
    }),
    ...(property.furnished && {
      accommodationCategory: property.furnished ? 'furnished' : 'unfurnished'
    }),
    ...(property.availableDate && {
      availabilityStarts: property.availableDate
    }),
    ...(property.guestCount && {
      occupancy: property.guestCount
    }),
    // Geo coordinates if available
    ...(property.latitude && property.longitude && {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: property.latitude,
        longitude: property.longitude
      }
    }),
    // Publisher information
    publisher: {
      '@id': `${SITE_URL}/${locale}#organization`
    }
  };

  // Add broker information if available
  if (property.agent || property.broker) {
    schema.broker = {
      '@type': 'RealEstateAgent',
      name: property.agent?.name || property.broker?.name || 'Prime Haus',
      url: `${SITE_URL}/${locale}/agent/${property.agentId || property.brokerId}`
    };
  }

  return schema;
}

/**
 * Generate FAQ schema for common real estate questions
 */
export function generateFAQSchema(faqItems: Array<{question: string, answer: string}>, locale: Locale) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: locale === 'es' ? 'es' : 'en',
    mainEntity: faqItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

/**
 * Generate Review schema for customer testimonials
 */
export function generateReviewSchema(review: {
  reviewBody: string;
  reviewerName: string;
  ratingValue: number;
  datePublished: string;
}, locale: Locale) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Review',
    reviewBody: review.reviewBody,
    reviewer: {
      '@type': 'Person',
      name: review.reviewerName,
    },
    reviewRating: {
      '@type': 'Rating',
      ratingValue: review.ratingValue,
    },
    datePublished: review.datePublished,
    inLanguage: locale === 'es' ? 'es' : 'en',
  };
}

/**
 * Generate breadcrumb structured data
 */
export function generateBreadcrumbSchema(items: { name: string; url: string }[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * Adds structured data script to the document head
 */
export function addStructuredData(data: object, id?: string) {
  // Remove existing structured data script if present
  if (id) {
    const existingScript = document.getElementById(id);
    if (existingScript) {
      existingScript.remove();
    }
  }

  const script = document.createElement('script');
  script.type = 'application/ld+json';
  if (id) {
    script.id = id;
  }
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}

/**
 * Removes structured data script from the document head
 */
export function removeStructuredData(id: string) {
  const existingScript = document.getElementById(id);
  if (existingScript) {
    existingScript.remove();
  }
}
